import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DevelopmentChartData } from '../charts';
import { ChartConfiguration, ChartDataset, ScatterDataPoint } from 'chart.js';
import { handleDevelopmentTimeTicks, transformPriceForChartAxis } from '../../functions';
import { externalTooltipHandler } from '../custom-hover-tooltip';
import { TranslateService } from '@ngx-translate/core';
import { VideoDetail } from 'src/app/pages/user/videos/videos';

const defaultPoint = {
  pointBackgroundColor: 'transparent',
  hoverBackgroundColor: 'transparent',
  pointBorderColor: 'transparent',
  pointHoverBackgroundColor: 'transparent',
  pointHoverBorderColor: 'transparent',
  pointHitRadius: 14,
}

const singleValuePoint = {
  pointHoverRadius: 11,
  pointBackgroundColor: '#fff',
  pointBorderColor: '#E52713',
  pointHoverBorderColor: '#E52713',
  pointHitRadius: 14,
  fill: 'origin',
}

@Component({
  selector: 'libero-development-chart',
  templateUrl: './development-chart.component.html',
  styleUrls: ['./development-chart.component.scss'],
  standalone: false
})
export class DevelopmentChartComponent implements OnChanges {
  @Input() public chartData!: DevelopmentChartData;
  @Input() public xLabels!: string[] | number[];
  @Input() public userLocalization: 'CZK' | 'EUR' = 'CZK';
  @Input() public previewVideo?: VideoDetail;

  public lineChartData?: ChartConfiguration['data'];

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    hover: {
      mode: 'index',
      intersect: true,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        position: 'nearest',
        external: (context) => externalTooltipHandler(context, 'developmentChart'),
      },
    },
    scales: {
      xAxis: {
        type: 'time',
        grid: {
          display: false,
        },
        ticks: {
          source: 'labels',
          callback: (label: string | number, index: number) => {
            return handleDevelopmentTimeTicks(index, this.xLabels, this.userLocalization, label)
          },
          autoSkip: false,
        },
      },
      yAxis: {
        beginAtZero: true,
        ticks: {
          callback: (tickValue: string | number) => {
            return transformPriceForChartAxis(tickValue)
          },
        }
      }
    },
    elements: {
      line: {
        borderWidth: 5,
      }
    }
  };

  public phoneChartOptions = {
    ...this.lineChartOptions,
    responsive: true,
  };

  public constructor(
    private translate: TranslateService
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    const datasets: ChartDataset[] = []

    const onlySingleValue = changes['chartData'].currentValue['deposits'].length === 1;


    if (changes['chartData']) {
      let pointsSettings: Record<string, number | string> = defaultPoint;
      if(onlySingleValue) {
        pointsSettings = singleValuePoint;
      }

      datasets.push(
        {
          label: this.translate.instant('chartLabels.deposits'),
          data: this.chartData.deposits as ScatterDataPoint[],
          borderColor: '#262626',
          backgroundColor: '#262626',
          ...pointsSettings,
        },
        {
          label: this.translate.instant('chartLabels.optimisticScenario'),
          data: this.chartData.optimisticScenario as ScatterDataPoint[],
          borderColor: '#3FA535',
          backgroundColor: '#3FA535',
          ...pointsSettings,
        },
        {
          label: this.translate.instant('chartLabels.pessimisticScenario'),
          data: this.chartData.pessimisticScenario as ScatterDataPoint[],
          borderColor: '#E52713',
          backgroundColor: '#E52713',
          ...pointsSettings,
        },
        {
          label: this.translate.instant('chartLabels.probableScenario'),
          data: this.chartData.probableScenario as ScatterDataPoint[],
          borderColor: '#BD9A43',
          backgroundColor: '#BD9A43',
          ...pointsSettings,
        },
      );
    }

    if (this.chartData && this.xLabels) {
      this.lineChartData = {
        datasets,
        labels: this.xLabels,
      }
    }
  }
}
